import React from "react";

import Badge from "components/UI/Badge";
import { isImageUrl } from "utils/cms/icon";

import type { HomeScreenSettingComponent, HomeScreenSettingItem } from "api/types/marketing.types";

interface HomeScreenDetailItemsProps {
  component: HomeScreenSettingComponent;
  onToggleItem: (itemId: number) => void;
  onDeleteItem: (itemId: number) => void;
  onEditItem: (item: HomeScreenSettingItem) => void;
  onOpenFeaturedDelete: (item: HomeScreenSettingItem) => void;
}

const formatPrice = (price: number) => price.toLocaleString("ko-KR") + "원";

const formatCountdown = (endAt?: string): string | null => {
  if (!endAt) return null;
  const end = new Date(endAt).getTime();
  const diff = end - Date.now();
  if (Number.isNaN(end)) return null;
  if (diff <= 0) return "마감";
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (days > 0) return `${days}일 ${hours}시간 남음`;
  if (hours > 0) return `${hours}시간 ${mins}분 남음`;
  return `${mins}분 남음`;
};

const remainingSeats = (capacity?: number, booked?: number) => {
  if (capacity == null) return null;
  const left = Math.max(capacity - (booked ?? 0), 0);
  if (left === 0) return { text: "마감", urgent: true };
  if (left <= 2) return { text: `마감임박 ${left}석`, urgent: true };
  return { text: `잔여 ${left}석`, urgent: false };
};

const HomeScreenDetailItems: React.FC<HomeScreenDetailItemsProps> = ({ component, onToggleItem, onDeleteItem, onEditItem, onOpenFeaturedDelete }) => {
  const type = component.component_type;
  const { items } = component;

  // 1) 버튼 / 퀵메뉴 → 단순 리스트
  if (type === "category" || type === "shortcut") {
    return (
      <ul className="home-screen__rows">
        {items.map((item) => (
          <li key={item.id} className={`home-screen__row ${!item.is_active ? "home-screen__row--disabled" : ""}`}>
            <span className="home-screen__row-icon">{item.icon ? isImageUrl(item.icon) ? <img src={item.icon} alt={item.title} /> : item.icon : "❓"}</span>
            <div className="home-screen__row-body">
              <span className="home-screen__row-title">{item.title}</span>
              {item.link && (
                <span className="home-screen__row-link" title={item.link}>
                  {item.link}
                </span>
              )}
            </div>
            <label className="toggle">
              <input type="checkbox" checked={item.is_active} onChange={() => onToggleItem(item.id)} />
              <span className="toggle__slider" />
            </label>
            <button className="home-screen__card-edit" onClick={() => onEditItem(item)} title="수정">
              수정
            </button>
            <button className="home-screen__card-remove" onClick={() => onDeleteItem(item.id)} title="삭제">
              삭제
            </button>
          </li>
        ))}
        {items.length === 0 && <p className="home-screen__empty">등록된 항목이 없습니다. 항목을 추가해주세요.</p>}
      </ul>
    );
  }

  // 2) SNS 갤러리 → masonry
  if (type === "gallery") {
    return (
      <div className="home-screen__gallery">
        {items.map((item) => (
          <div key={item.id} className={`home-screen__gallery-item ${!item.is_active ? "home-screen__gallery-item--disabled" : ""}`}>
            <div className="home-screen__gallery-thumb">
              {item.img ? <img src={item.img} alt={item.caption ?? item.title} /> : <div className="home-screen__card-placeholder">이미지 없음</div>}
              {item.source_type && item.source_type !== "internal" && <span className={`home-screen__gallery-source home-screen__gallery-source--${item.source_type}`}>{item.source_type === "instagram" ? "IG" : "YT"}</span>}
            </div>
            <div className="home-screen__gallery-meta">
              {item.caption && <p className="home-screen__gallery-caption">{item.caption}</p>}
              {item.author && <span className="home-screen__gallery-author">{item.author}</span>}
              {item.tag_labels && item.tag_labels.length > 0 && (
                <div className="home-screen__gallery-tags">
                  {item.tag_labels.map((t) => (
                    <span key={t} className="home-screen__gallery-tag">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
              <div className="home-screen__gallery-actions">
                <label className="toggle">
                  <input type="checkbox" checked={item.is_active} onChange={() => onToggleItem(item.id)} />
                  <span className="toggle__slider" />
                </label>
                <button className="home-screen__card-edit" onClick={() => onEditItem(item)}>
                  수정
                </button>
                <button className="home-screen__card-remove" onClick={() => onDeleteItem(item.id)}>
                  삭제
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="home-screen__empty">등록된 항목이 없습니다. 항목을 추가해주세요.</p>}
      </div>
    );
  }

  // 3) 나머지 (banner/featured/event/review/ranking/instructor/tour 등) → 카드 그리드
  return (
    <div className="home-screen__cards">
      {items.map((item) => {
        const remaining = type === "tour" ? remainingSeats(item.capacity, item.booked_count) : null;
        const countdown = type === "card" ? formatCountdown(item.end_at) : null;
        return (
          <div key={item.id} className={`home-screen__card ${!item.is_active ? "home-screen__card--disabled" : ""}`}>
            <div className="home-screen__card-image">
              {item.img ? <img src={item.img} alt={item.title} /> : <div className="home-screen__card-placeholder">이미지 없음</div>}
              {item.type && <Badge variant={item.type === "lesson" ? "info" : "active"}>{item.type === "lesson" ? "강습" : "상품"}</Badge>}
              {type === "tour" && remaining && <span className={`home-screen__card-remaining${remaining.urgent ? " home-screen__card-remaining--urgent" : ""}`}>{remaining.text}</span>}
              {type === "card" && item.badge && <span className={`home-screen__card-event-badge home-screen__card-event-badge--${item.badge_color ?? "red"}`}>{item.badge}</span>}
              {type === "card" && countdown && <span className="home-screen__card-countdown">⏱ {countdown}</span>}
            </div>
            <div className="home-screen__card-body">
              <h4 className="home-screen__card-title">{item.title}</h4>
              {type === "instructor" && item.brand_name && <span className="home-screen__card-brand">{item.brand_name}</span>}
              {item.description && <p className="home-screen__card-desc">{item.description}</p>}
              {type === "tour" && (
                <>
                  {item.region && <span className="home-screen__card-meta">📍 {item.region}</span>}
                  {item.start_date && item.end_date && (
                    <span className="home-screen__card-meta home-screen__card-meta--strong">
                      {item.start_date} ~ {item.end_date}
                    </span>
                  )}
                </>
              )}
              {type === "instructor" && (
                <div className="home-screen__card-chips">
                  {item.sports?.map((s) => (
                    <span key={s} className="home-screen__card-chip home-screen__card-chip--sport">
                      {s}
                    </span>
                  ))}
                  {item.certificates?.map((c) => (
                    <span key={c} className="home-screen__card-chip home-screen__card-chip--cert">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {type === "card" && item.start_at && item.end_at && (
                <span className="home-screen__card-meta">
                  {item.start_at.replace("T", " ")} ~ {item.end_at.replace("T", " ")}
                </span>
              )}
              {item.link && (
                <span className="home-screen__card-link" title={item.link}>
                  {item.link}
                </span>
              )}
              {item.price != null && (
                <div className="home-screen__card-price">
                  {(item.discount ?? 0) > 0 && item.origin_price != null && <span className="home-screen__card-origin-price">{formatPrice(item.origin_price)}</span>}
                  <span className="home-screen__card-final-price">{formatPrice(item.price)}</span>
                  {(item.discount ?? 0) > 0 && <span className="home-screen__card-discount">{item.discount}%</span>}
                </div>
              )}
            </div>
            <div className="home-screen__card-actions">
              {type !== "featured" && (
                <label className="toggle">
                  <input type="checkbox" checked={item.is_active} onChange={() => onToggleItem(item.id)} />
                  <span className="toggle__slider" />
                </label>
              )}
              {type !== "featured" && (
                <button className="home-screen__card-edit" onClick={() => onEditItem(item)} title="수정">
                  수정
                </button>
              )}
              <button className="home-screen__card-remove" onClick={() => (type === "featured" ? onOpenFeaturedDelete(item) : onDeleteItem(item.id))} title="삭제">
                삭제
              </button>
            </div>
          </div>
        );
      })}
      {items.length === 0 && <p className="home-screen__empty">등록된 항목이 없습니다. 항목을 추가해주세요.</p>}
    </div>
  );
};

export default HomeScreenDetailItems;
